import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import type { ContextMenuItem } from '@/components/ui/ContextMenu';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  EyeSlashIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import { useUIStore } from '@/stores/ui/ui-store';
import { useToastStore } from '@/stores/toast-store';
import {
  buildSelectionText,
  copyTsvToClipboard,
  exportSelectionToCsv,
  makeCsvFilename,
  type CsvTableName,
} from '@/utils/cell-selection-utils';
import { MOD_KEY, MOD_ALT_KEY } from '@/utils/platform';

type ColumnPinSide = 'left' | 'right' | null;
type ColumnConfig = { id: string; label: string; visible: boolean };

/* ---------- Configuration types ---------- */

export interface TableContextMenuFilterConfig<TItem extends object, TFilters> {
  /** Set of filterable column IDs */
  filterableColumnIds: Set<string>;
  /** Map column ID → key in the filter state for array-based "filter by value" */
  arrayFilterMap?: Partial<Record<string, keyof TFilters>>;
  /** Map column ID → {min, max} keys in the filter state for numeric "filter by value" */
  numericFilterMap?: Partial<Record<string, { min: keyof TFilters; max: keyof TFilters }>>;
  /**
   * The number a numeric "filter by value" should WRITE for a column. Defaults to
   * the raw `item[columnId]`, which is right whenever the cell renders that field
   * verbatim. Override it wherever the cell shows something derived, or the menu
   * ends up labelled with one number and filtering on another (LR-628:
   * `daily_earnings` renders `effective_daily_earnings ?? daily_earnings`).
   */
  getNumericFilterValue?: (columnId: string, item: TItem) => number | null;
  /** Optional map from display column ID → raw item field (e.g. status_display → status) */
  displayToRawField?: Record<string, keyof TItem>;
  /** Columns to skip for array "filter by value" (e.g. counterparties in availability) */
  skipArrayFilterColumns?: Set<string>;
  /** Custom cell display value formatter. Falls back to String(item[columnId]) */
  getCellDisplayValue?: (columnId: string, item: TItem) => string;
  /** Current filter state */
  filters: TFilters;
  /** Setter for filter state */
  setFilters: (filters: TFilters) => void;
}

export interface UseTableContextMenusParams<TItem extends object, TFilters> {
  columnConfig: ColumnConfig[];
  columns: ColumnDef<TItem, unknown>[];
  sorting: { id: string; desc: boolean }[];
  /** Columns the table can sort; the others get no Sort items. Default: all. */
  isSortable?: (columnId: string) => boolean;
  setSorting: ((sorting: { id: string; desc: boolean }[]) => void) | React.Dispatch<React.SetStateAction<{ id: string; desc: boolean }[]>>;
  getPinState: (columnId: string) => ColumnPinSide;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  toggleColumnVisibility: (columnId: string) => void;
  handleFilterColumnClick: (columnId: string) => void;
  sortedDataRef: React.MutableRefObject<TItem[]>;
  filterConfig: TableContextMenuFilterConfig<TItem, TFilters>;
  /** Column IDs in visual order (from computeVisualColIds) */
  colIds: string[];
  /** External selection state from useCellSelection */
  selectedCellsForCopy: () => Set<string>;
  selectedCellCount: () => number;
  selectColumn: (colIndex: number, additive: boolean) => void;
  deselectColumn: (colIndex: number) => void;
  isColumnFullySelected: (colIndex: number) => boolean;
  colIdToIndex: Map<string, number>;
  /** Used as prefix in exported CSV filename, e.g. 'trades' → 'trades_2026-04-14_143022.csv' */
  tableName: CsvTableName;
}

/* ---------- Hook ---------- */

export function useTableContextMenus<TItem extends object, TFilters>({
  columnConfig,
  columns,
  sorting,
  isSortable,
  setSorting,
  getPinState,
  pinColumn,
  toggleColumnVisibility,
  handleFilterColumnClick,
  sortedDataRef,
  filterConfig,
  colIds,
  selectedCellsForCopy,
  selectedCellCount,
  selectColumn,
  deselectColumn,
  isColumnFullySelected,
  colIdToIndex,
  tableName,
}: UseTableContextMenusParams<TItem, TFilters>) {
  const {
    filterableColumnIds,
    arrayFilterMap = {},
    numericFilterMap = {},
    getNumericFilterValue = (columnId, item) =>
      (item as Record<string, unknown>)[columnId] as number | null,
    displayToRawField = {},
    skipArrayFilterColumns,
    getCellDisplayValue: customGetCellDisplayValue,
    filters,
    setFilters,
  } = filterConfig;

  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((s) => s.setColumnsTabActive);
  const setFilterSidebarCollapsed = useUIStore((s) => s.setFilterSidebarCollapsed);
  const addToast = useToastStore((s) => s.addToast);
  const setSidebarHighlightColumnId = useUIStore((s) => s.setSidebarHighlightColumnId);

  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const flashColumnInSidebar = useCallback((columnId: string) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setFilterSidebarCollapsed(false);
    setSidebarActiveTab('columns');
    setColumnsTabActive(true);
    setSidebarHighlightColumnId(columnId);
    highlightTimerRef.current = setTimeout(() => setSidebarHighlightColumnId(null), 2000);
  }, [setFilterSidebarCollapsed, setSidebarActiveTab, setColumnsTabActive, setSidebarHighlightColumnId]);

  // Header context menu
  const [headerCtxMenu, setHeaderCtxMenu] = useState<{ columnId: string; x: number; y: number } | null>(null);
  const handleColumnContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setHeaderCtxMenu({ columnId, x: e.clientX, y: e.clientY });
    },
    []
  );
  const closeHeaderCtxMenu = useCallback(() => setHeaderCtxMenu(null), []);

  const headerCtxMenuItems = useMemo((): ContextMenuItem[] => {
    if (!headerCtxMenu) return [];
    const colId = headerCtxMenu.columnId;
    const colConfig = columnConfig.find((c) => c.id === colId);
    const label = colConfig?.label ?? colId;
    const currentSort = sorting.find((s) => s.id === colId);
    const isFilterable = filterableColumnIds.has(colId);
    const pin = getPinState(colId);
    const items: ContextMenuItem[] = isSortable && !isSortable(colId) ? [] : [
      {
        label: 'Sort ascending',
        icon: <ArrowUpIcon className="h-3.5 w-3.5" />,
        checked: !!currentSort && !currentSort.desc,
        onClick: () => setSorting([{ id: colId, desc: false }]),
      },
      {
        label: 'Sort descending',
        icon: <ArrowDownIcon className="h-3.5 w-3.5" />,
        checked: !!currentSort && currentSort.desc,
        onClick: () => setSorting([{ id: colId, desc: true }]),
      },
      ...(currentSort ? [{
        label: 'Clear sort',
        icon: <XMarkIcon className="h-3.5 w-3.5" />,
        onClick: () => setSorting(sorting.filter((s) => s.id !== colId)),
      }] : []),
    ];
    if (isFilterable) {
      items.push({
        label: `Filter by ${label}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        dividerBefore: items.length > 0,
        onClick: () => handleFilterColumnClick(colId),
      });
    }
    if (colId !== 'rowNumber') {
      items.push(
        {
          label: 'Pin left',
          icon: <PinLeftIcon className="h-3.5 w-3.5" />,
          dividerBefore: !isFilterable && items.length > 0,
          checked: pin === 'left',
          onClick: () => { pinColumn(colId, pin === 'left' ? null : 'left'); flashColumnInSidebar(colId); },
        },
        {
          label: 'Pin right',
          icon: <PinRightIcon className="h-3.5 w-3.5" />,
          checked: pin === 'right',
          onClick: () => { pinColumn(colId, pin === 'right' ? null : 'right'); flashColumnInSidebar(colId); },
        },
      );
      if (pin) {
        items.push({
          label: 'Unpin',
          icon: <XMarkIcon className="h-3.5 w-3.5" />,
          onClick: () => { pinColumn(colId, null); flashColumnInSidebar(colId); },
        });
      }
      items.push({
        label: 'Hide column',
        icon: <EyeSlashIcon className="h-3.5 w-3.5" />,
        dividerBefore: true,
        onClick: () => { toggleColumnVisibility(colId); flashColumnInSidebar(colId); },
      });
    }
    // Select / deselect column
    const ci = colIdToIndex.get(colId);
    if (ci !== undefined) {
      const colFullySelected = isColumnFullySelected(ci);
      if (colFullySelected) {
        items.push({
          label: 'Deselect column',
          icon: <ViewColumnsIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => deselectColumn(ci),
        });
      } else {
        const cellCount = selectedCellCount();
        const additive = cellCount > 1;
        items.push({
          label: additive ? 'Add column to selection' : 'Select column',
          icon: <ViewColumnsIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => selectColumn(ci, additive),
        });
      }
    }
    return items;
  }, [headerCtxMenu, columnConfig, sorting, isSortable, getPinState, pinColumn, toggleColumnVisibility, handleFilterColumnClick, setSorting, flashColumnInSidebar, filterableColumnIds, colIdToIndex, selectColumn, deselectColumn, isColumnFullySelected, selectedCellCount]);

  // Cell context menu
  const [cellCtxMenu, setCellCtxMenu] = useState<{ columnId: string; item: TItem; x: number; y: number } | null>(null);
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string, item: TItem) => {
      e.preventDefault();
      e.stopPropagation();
      setCellCtxMenu({ columnId, item, x: e.clientX, y: e.clientY });
    },
    []
  );
  const closeCellCtxMenu = useCallback(() => setCellCtxMenu(null), []);

  const getCellDisplayValue = useCallback((columnId: string, item: TItem): string => {
    if (customGetCellDisplayValue) {
      return customGetCellDisplayValue(columnId, item);
    }
    const value = (item as Record<string, unknown>)[columnId];
    if (value === null || value === undefined) return '';
    return String(value);
  }, [customGetCellDisplayValue]);

  const cellCtxMenuItems = useMemo((): ContextMenuItem[] => {
    if (!cellCtxMenu) return [];
    const { columnId, item } = cellCtxMenu;
    const colConfig = columnConfig.find((c) => c.id === columnId);
    const label = colConfig?.label ?? columnId;
    const displayValue = getCellDisplayValue(columnId, item);
    const truncatedValue = displayValue.length > 30 ? displayValue.slice(0, 30) + '...' : displayValue;

    // Multi-cell selection context menu
    const cellCount = selectedCellCount();
    if (cellCount > 1) {
      const cellSet = selectedCellsForCopy();
      const formatValue = (rowItem: TItem, colId: string) => getCellDisplayValue(colId, rowItem);
      const getHeader = (colId: string) => columnConfig.find((c) => c.id === colId)?.label ?? colId;
      return [
        {
          label: `Copy ${cellCount} cells`,
          icon: <ClipboardDocumentIcon className="h-3.5 w-3.5" />,
          shortcut: `${MOD_KEY}+C`,
          onClick: () => {
            const text = buildSelectionText(cellSet, colIds, colIdToIndex, sortedDataRef.current, { formatValue });
            navigator.clipboard.writeText(text).then(() => addToast('success', `Copied ${cellCount} cells`)).catch(() => {});
          },
        },
        {
          label: 'Copy with headers',
          icon: <ClipboardDocumentListIcon className="h-3.5 w-3.5" />,
          shortcut: `${MOD_ALT_KEY}+C`,
          onClick: () => {
            const text = buildSelectionText(cellSet, colIds, colIdToIndex, sortedDataRef.current, { formatValue, withHeaders: true, getHeader });
            navigator.clipboard.writeText(text).then(() => addToast('success', `Copied ${cellCount} cells with headers`)).catch(() => {});
          },
        },
        {
          label: `Export ${cellCount} cells to CSV`,
          icon: <ArrowDownTrayIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => {
            exportSelectionToCsv(
              cellSet, colIds, colIdToIndex, sortedDataRef.current,
              { formatValue, withHeaders: true, getHeader },
              makeCsvFilename(tableName),
            );
            addToast('success', `Exported ${cellCount} cells to CSV`);
          },
        },
      ];
    }

    const items: ContextMenuItem[] = [
      {
        label: 'Copy',
        icon: <ClipboardDocumentIcon className="h-3.5 w-3.5" />,
        shortcut: `${MOD_KEY}+C`,
        onClick: () => { copyTsvToClipboard([[displayValue]]).then(() => addToast('success', 'Copied 1 cell')).catch(() => {}); },
      },
      {
        label: 'Copy with header',
        icon: <ClipboardDocumentListIcon className="h-3.5 w-3.5" />,
        shortcut: `${MOD_ALT_KEY}+C`,
        onClick: () => { copyTsvToClipboard([[label], [displayValue]]).then(() => addToast('success', 'Copied 1 cell with header')).catch(() => {}); },
      },
      {
        label: 'Copy entire row',
        icon: <TableCellsIcon className="h-3.5 w-3.5" />,
        onClick: () => {
          const visibleCols = columns.filter((c) => {
            const cid = getColumnId(c);
            return cid !== 'rowNumber';
          });
          const headers = visibleCols.map((c) => {
            const cid = getColumnId(c);
            const cfg = columnConfig.find((cc) => cc.id === cid);
            return cfg?.label ?? cid;
          });
          const values = visibleCols.map((c) => {
            const cid = getColumnId(c);
            return getCellDisplayValue(cid, item);
          });
          copyTsvToClipboard([headers, values]).then(() => addToast('success', `Copied row (${values.length} cells)`)).catch(() => {});
        },
      },
      {
        label: 'Export cell to CSV',
        icon: <ArrowDownTrayIcon className="h-3.5 w-3.5" />,
        dividerBefore: true,
        onClick: () => {
          const cellSet = selectedCellsForCopy();
          if (cellSet.size === 0) return;
          const formatValue = (rowItem: TItem, colId: string) => getCellDisplayValue(colId, rowItem);
          const getHeader = (colId: string) => columnConfig.find((c) => c.id === colId)?.label ?? colId;
          exportSelectionToCsv(
            cellSet, colIds, colIdToIndex, sortedDataRef.current,
            { formatValue, withHeaders: true, getHeader },
            makeCsvFilename(tableName),
          );
          addToast('success', 'Exported 1 cell to CSV');
        },
      },
    ];

    // "Filter by value" for multi-select columns
    const arrayKey = arrayFilterMap[columnId];
    if (arrayKey && displayValue) {
      if (!skipArrayFilterColumns || !skipArrayFilterColumns.has(columnId)) {
        const rawField = displayToRawField[columnId];
        const filterValue = rawField ? String((item as Record<string, unknown>)[rawField as string] ?? displayValue) : displayValue;
        const currentArr = (filters as Record<string, string[]>)[arrayKey as string];
        const isAlreadyFiltered = currentArr.includes(filterValue);
        items.push({
          label: isAlreadyFiltered ? `Remove filter "${truncatedValue}"` : `Filter by "${truncatedValue}"`,
          icon: <FunnelIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => {
            setFilters({
              ...filters,
              [arrayKey]: isAlreadyFiltered
                ? currentArr.filter((v) => v !== filterValue)
                : [...currentArr, filterValue],
            });
          },
        });
      }
    }

    // "Filter by value" for numeric columns
    const numericKeys = numericFilterMap[columnId];
    const rawValue = getNumericFilterValue(columnId, item);
    if (numericKeys && typeof rawValue === 'number') {
      items.push({
        label: `Filter ${truncatedValue} in ${label}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        dividerBefore: !arrayKey,
        submenu: [
          {
            label: `Equals ${truncatedValue}`,
            onClick: () => {
              setFilters({
                ...filters,
                [numericKeys.min]: rawValue,
                [numericKeys.max]: rawValue,
              });
            },
          },
          {
            label: `Less than ${truncatedValue}`,
            onClick: () => {
              setFilters({
                ...filters,
                [numericKeys.min]: null,
                [numericKeys.max]: rawValue,
              });
            },
          },
          {
            label: `Greater than ${truncatedValue}`,
            onClick: () => {
              setFilters({
                ...filters,
                [numericKeys.min]: rawValue,
                [numericKeys.max]: null,
              });
            },
          },
        ],
      });
    }

    return items;
  }, [cellCtxMenu, selectedCellCount, selectedCellsForCopy, columnConfig, columns, getCellDisplayValue, filters, setFilters, sortedDataRef, arrayFilterMap, numericFilterMap, getNumericFilterValue, displayToRawField, skipArrayFilterColumns, colIds, colIdToIndex, tableName, addToast]);

  // Close context menus on table scroll
  const bindScrollClose = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const handleScroll = () => {
      setHeaderCtxMenu(null);
      setCellCtxMenu(null);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  return {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
    cellCtxMenu,
    cellCtxMenuItems,
    handleCellContextMenu,
    closeCellCtxMenu,
    bindScrollClose,
  };
}
